import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import eventBrandingImg from '../assets/images/event-branding.jpg';
import visualIdentityVideo from '../assets/videos/ojodu-works.mp4';

const ServiceCard = ({
  title,
  description,
  includes,
  price,
  link,
  image,
  video,
  isFullWidth = false
}) => {
  const CardContent = () => (
    <div className={`group relative overflow-hidden rounded-2xl bg-[#1a1a1a] border border-white/10 hover:border-[#A2CD3C]/50 transition-all duration-500 ${isFullWidth ? 'lg:col-span-2' : ''}`}>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80 z-10" />

      {/* Background Media */}
      <div className="absolute inset-0 z-0">
        {video ? (
          <div className="w-full h-full relative">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-500"
            >
              <source src={video} type="video/mp4" />
            </video>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-white/50 text-sm font-light">[Video: {title}]</span>
            </div>
          </div>
        ) : image ? (
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full bg-[#2a2a2a]" />
        )}
      </div>

      <div className="relative z-20 p-8 h-full flex flex-col justify-end min-h-[400px]">
        <div className="mb-auto">
          <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#A2CD3C] transition-colors">{title}</h3>
          <p className="text-gray-300 text-sm leading-relaxed mb-6 max-w-md">{description}</p>

          <div className="space-y-2 mb-6">
            <p className="text-xs font-semibold text-[#A2CD3C] uppercase tracking-wider">Includes:</p>
            <ul className="grid grid-cols-1 gap-2">
              {includes.map((item, idx) => (
                <li key={idx} className="text-gray-400 text-sm flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#A2CD3C]" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/10">
          <p className="text-white font-medium">{price}</p>
          {link && (
            <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#A2CD3C] transition-colors">
              <ArrowUpRight size={20} className="text-white" />
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return link ? (
    <a href={link} target="_blank" rel="noopener noreferrer" className={isFullWidth ? 'lg:col-span-2' : ''}>
      <CardContent />
    </a>
  ) : (
    <div className={isFullWidth ? 'lg:col-span-2' : ''}>
      <CardContent />
    </div>
  );
};

export const ServicesSection = () => {
  const services = [
    {
      title: "Brand & Visual Identity",
      description: "We develop brand systems that are clear, scalable, and culturally relevant.",
      includes: [
        "Brand strategy & positioning",
        "Logo & visual identity systems",
        "Brand guidelines",
        "Campaign visual direction"
      ],
      price: "Starting from ₦300,000",
      link: "https://www.behance.net/gallery/243537885/OjoduWorks-Visual-Identity",
      video: visualIdentityVideo
    },
    {
      title: "Media Design & Content Production",
      description: "We create media assets that communicate, persuade, and perform across channels.",
      includes: [
        "Social media design & content",
        "Motion graphics & short-form videos",
        "Campaign creatives",
        "Presentation & pitch materials"
      ],
      price: "Starting from 350k/ Month"
    },
    {
      title: "Campaign & Creative Direction",
      description: "We support brands with end-to-end creative direction for launches, events, and marketing campaigns.",
      includes: [
        "Creative concepts",
        "Campaign visuals & rollout assets",
        "Media consistency across platforms",
        "Vendor & production supervision"
      ],
      price: "Starting from 400k/one-off"
    },
    {
      title: "Social Media Management",
      description: "We manage and grow brand presence across social platforms through strategic content, consistent visuals, and audience-focused storytelling.",
      includes: [
        "Content strategy & planning",
        "Social media design & content creation",
        "Community engagement support",
        "Performance tracking & content optimisation"
      ],
      price: "Custom Quote"
    },
    {
      title: "Event Branding",
      description: "We design and manage cohesive visual experiences for events, ensuring brand consistency across physical and digital touchpoints.",
      includes: [
        "Event visual identity & theme direction",
        "Stage, backdrop & environmental branding",
        "Digital & print event assets",
        "On-ground branding supervision"
      ],
      price: "Custom Quote",
      link: "https://www.behance.net/gallery/228659835/International-Widows-Day-2025-Chadash",
      image: eventBrandingImg,
      isFullWidth: true
    }
  ];

  return (
    <section id="services" className="section-padding bg-[#111111]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16 md:text-center max-w-3xl mx-auto">
          <div className="section-divider mb-6 md:mx-auto" />
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Engineered Visibility for <span className="gradient-text">Every Industry</span>
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed">
            From emerging brands to large organisations, Anel supports clients across multiple sectors with media solutions designed for clarity, consistency, and scale. Our approach adapts to industry needs while maintaining a strong focus on results, relevance, and audience connection.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </div>
      </div>
    </section>
  );
};
